import React, { useEffect, useState } from "react";
import {
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
  IconButton,
  Button,
  Tooltip
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import TimeAutocomplete from "../TimeAutocomplete";
import { objectLength } from "@/utils/helperFunctions";

export interface ShiftType {
  name: string;
  startTime: string;
  endTime: string;
}

interface ProjectShiftsProps {
  settingName: string;
  settingValue: string;
  handleUpdate: (settingValue: string) => void;
  editMode?: boolean;
  kickOffMode?: boolean;
  defaultChecked?: boolean;
}

const parseShifts = (value: string): ShiftType[] => {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const ProjectShifts: React.FC<ProjectShiftsProps> = ({
  settingName,
  settingValue,
  handleUpdate,
  editMode,
  kickOffMode,
  defaultChecked = false
}) => {
  const [projectShifts, setProjectShifts] = useState<ShiftType[]>(() => parseShifts(settingValue));
  const [enableProjectShifts, setEnableProjectShifts] = useState<boolean>(defaultChecked);

  // for keeping local UI state aligned with parent value (since otherwise it goes out of sync
  // when the query responds a little late)
  useEffect(() => {
    setEnableProjectShifts(defaultChecked);
  }, [defaultChecked]);

  // for keeping local UI state aligned with parent value (since otherwise it goes out of sync
  // when the query responds a little late)
  useEffect(() => {
    if (settingValue) setProjectShifts(parseShifts(settingValue));
  }, [settingValue]);

  const handleDefaultShifts = (checked: boolean) => {
    setEnableProjectShifts(checked);
    const newValue = checked ? JSON.stringify(projectShifts) : "";
    handleUpdate(newValue);
  };

  const handleShiftChange = (index: number, field: keyof ShiftType, value: string) => {
    const updatedShifts = [...projectShifts];
    updatedShifts[index] = { ...updatedShifts[index], [field]: value };

    setProjectShifts(updatedShifts);
    handleUpdate(JSON.stringify(updatedShifts));
  };

  const handleTimeChange = (index: number, field: 'startTime' | 'endTime', value: string | null) => {
    const timeString = value || "";

    const updatedShifts = [...projectShifts];
    updatedShifts[index] = { ...updatedShifts[index], [field]: timeString };

    setProjectShifts(updatedShifts);
    handleUpdate(JSON.stringify(updatedShifts));
  };

  const handleAddShift = () => {
    const newShift: ShiftType = {
      name: '',
      startTime: '09:00',
      endTime: '17:00'
    };
    setProjectShifts([...projectShifts, newShift]);
    handleUpdate(JSON.stringify([...projectShifts, newShift]));
  };

  const handleDeleteShift = (index: number) => {
    const updatedShifts = projectShifts.filter((_, i) => i !== index);
    setProjectShifts(updatedShifts);
    handleUpdate(JSON.stringify(updatedShifts));
  };

  return (
    <Grid container spacing={2}>
      {!(editMode || kickOffMode) && (
        <Grid display="flex" alignItems="center" size={12}>
          <FormControlLabel
            label={settingName}
            control={
              <Switch
                checked={enableProjectShifts}
                onChange={(_, checked) => handleDefaultShifts(checked)}
                color="primary"
              />
            }
          />
        </Grid>
      )}
      {(enableProjectShifts || editMode || kickOffMode) && (
        <Grid size={12}>
          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
            Available Shifts:
          </Typography>
          {objectLength(projectShifts) > 0 ?
            projectShifts.map((shift, index) => (
              <Grid container key={index} sx={{ mb: 2 }} alignItems="baseline" justifyContent='space-between' spacing={1}>
                <Grid size={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Shift Name"
                    value={shift.name}
                    onChange={(e) => handleShiftChange(index, "name", e.target.value)}
                    error={shift.name.trim() === ""}
                    helperText={shift.name.trim() === "" ? "Required" : ""}
                  />
                </Grid>

                <Grid size={3}>
                  <TimeAutocomplete
                    time={shift.startTime}
                    label="Start Time"
                    onTimeChange={(newValue) => handleTimeChange(index, 'startTime', newValue)}
                  />
                </Grid>

                <Grid size={3}>
                  <TimeAutocomplete
                    time={shift.endTime}
                    label="End Time"
                    onTimeChange={(newValue) => handleTimeChange(index, 'endTime', newValue)}
                  />
                </Grid>

                <Grid size="auto">
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteShift(index)}
                    size="small"
                  >
                    <Tooltip title="Delete Shift">
                      <DeleteIcon />
                    </Tooltip>
                  </IconButton>
                </Grid>
              </Grid>
            )) :
            <Typography variant="body2" color="textSecondary" textAlign="center">Add new shift</Typography>
          }

        </Grid>
      )}
      {(enableProjectShifts || editMode || kickOffMode) && (
        <Grid container justifyContent="flex-end" size={12}>
          <Button
            color="primary"
            size="small"
            onClick={handleAddShift}
            variant="outlined"
            disabled={projectShifts.some(shift => shift.name.trim() === "")}
          >
            <Tooltip title="Add Shift">
              <AddIcon />
            </Tooltip>
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default ProjectShifts;
